<script setup lang="ts">
import { ref } from 'vue';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { toast } from 'vue-sonner';
import * as Sentry from '@sentry/vue';
import { useMutation } from '@tanstack/vue-query';
import { AxiosError } from 'axios';

interface BugReportData {
  name?: string;
  email?: string;
  message: string;
}

interface BugReportErrors {
  message?: string[];
  [key: string]: string[] | undefined;
}

interface Props {
  user: {
    name?: string;
    email?: string;
  };
  open: boolean;
}

interface Emits {
  (e: 'update:open', value: boolean): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const description = ref('');
const errors = ref<BugReportErrors>({});

const { mutate: sendFeedback, isPending: isSubmitting } = useMutation({
  mutationFn: (data: BugReportData) => {
    return Sentry.sendFeedback(
      {
        name: data.name,
        email: data.email,
        message: data.message,
      },
      {
        includeReplay: true,
      }
    );
  },
  onSuccess: () => {
    toast.success('Thank you for your feedback.', { duration: 6000 });
    resetForm();
    closeDialog();
  },
  onError: (error: unknown) => {
    if (error instanceof AxiosError && error.response?.status === 422) {
      errors.value = error.response.data.errors;
    } else {
      errors.value = {};
    }
    toast.error('There was an issue submitting your bug report.', { duration: 6000 });
  }
});

function resetForm() {
  description.value = '';
  errors.value = {};
}

function closeDialog() {
  emit('update:open', false);
}

function submitBugReport() {
  if (!description.value.trim()) {
    toast.warning('A description is required.', { duration: 6000 });
    return;
  }

  sendFeedback({
    name: props.user.name,
    email: props.user.email,
    message: description.value.trim()
  });
}
</script>

<template>
  <Dialog :open="open" @update:open="(value) => emit('update:open', value)">
    <DialogContent>
      <DialogHeader>
        <DialogTitle>Submit a Bug Report</DialogTitle>
      </DialogHeader>
      <DialogDescription>
        Please describe the bug you're experiencing.
      </DialogDescription>
      <form @submit.prevent="submitBugReport">
        <Textarea 
          id="description" 
          :modelValue="description"
          @update:modelValue="(value: string | number) => description = String(value)"
          autoResize 
          rows="5" 
          cols="30"
          placeholder="Describe the bug you encountered..."
          :disabled="isSubmitting"
          required
        />
        <div v-if="errors['message']" class="mt-2 text-sm text-destructive">
          {{ errors['message'][0] }}
        </div>
      </form>
      <DialogFooter>
        <DialogClose as-child>
          <Button variant="secondary" :disabled="isSubmitting">Cancel</Button>
        </DialogClose>
        <Button 
          @click="submitBugReport"
          :disabled="isSubmitting"
          :loading="isSubmitting"
        >
          Submit
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template> 